import { ReactNode, useEffect, useState, FC } from 'react';
import { IntegrationAppProvider } from '@integration-app/react';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Loading } from 'src/components/Loading';
import { IntegrationAppProvidersProvider } from 'src/contexts/IntegrationAppProvidersContext';

export function useIntegrationAppToken(): string | null {
  const { get } = useAuthedRequest();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // if token is already set, don't fetch it again
    if (token) return;

    let cancelled = false;

    const fetchToken = async () => {
      try {
        const response = await get(
          '/account-connections/integration-app/token',
        );
        if (!cancelled && response.token) {
          setToken(response.token);
        }
      } catch (error) {
        console.error('Error fetching integration app token', error);
        if (!cancelled) {
          setToken('');
        }
      }
    };

    fetchToken();

    return () => {
      cancelled = true;
    };
  }, [get]);

  return token;
}

/**
 * Wraps any children in an IntegrationAppProvider once the token is ready.
 * Shows a spinner (or fallback) while loading.
 */
export const IntegrationAppWrapper: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const token = useIntegrationAppToken();

  if (token === null) {
    // still loading
    return (
      <div className="flex items-center justify-center h-full">
        <Loading size="8" />
      </div>
    );
  }

  if (token === '') {
    // errored fetching token
    return <div className="p-4 text-error">Failed to load page.</div>;
  }

  return (
    <IntegrationAppProvider token={token}>
      <IntegrationAppProvidersProvider>
        {children}
      </IntegrationAppProvidersProvider>
    </IntegrationAppProvider>
  );
};

/**
 * HOC: wrap any component in IntegrationAppWrapper automatically.
 * Usage: export default withIntegrationApp(MyComponent)
 */
export function withIntegrationApp<T extends React.ComponentType<any>>(
  WrappedComponent: T,
): React.ComponentType<React.ComponentProps<T>> {
  // Pull the exact props type from the wrapped component
  type Props = React.ComponentProps<T>;

  const WithIntegration: React.FC<Props> = (props) => (
    <IntegrationAppWrapper>
      <WrappedComponent {...props} />
    </IntegrationAppWrapper>
  );

  WithIntegration.displayName = `WithIntegrationApp(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithIntegration;
}
